import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Input,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Select,
  Tag,
  Typography,
} from 'antd'
import {
  FormOutlined,
  LinkOutlined,
  ShoppingOutlined,
  SwapOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import styled from 'styled-components/macro'
import { API } from 'src/utils/interceptor'
import { productsForLinkedProfileEdit } from 'src/utils/linkedProfileProducts'

const { Text } = Typography

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const WayfindingBanner = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #d3adf7;
  background: linear-gradient(90deg, #f9f0ff 0%, #f0f9ff 100%);
  font-size: 12px;
  line-height: 1.5;
  color: #434343;
`

const Section = styled.section`
  min-width: 0;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $variant }) => ($variant === 'list' ? '#91caff' : '#d3adf7')};
  background: ${({ $variant }) =>
    $variant === 'list'
      ? 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%)'
      : 'linear-gradient(180deg, #faf5ff 0%, #ffffff 100%)'};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
`

const SectionIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
  background: ${({ $variant }) =>
    $variant === 'list' ? '#1677ff' : '#722ed1'};
  font-size: 14px;
`

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const SectionHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  margin-top: 2px;
  line-height: 1.45;
`

const GroupsList = styled(List)`
  .ant-list-item {
    padding: 12px !important;
    border: 1px solid #e6f4ff !important;
    border-radius: 8px !important;
    margin-bottom: 8px !important;
    background: #ffffff;
    display: block !important;
  }

  .ant-list-item:last-child {
    margin-bottom: 0 !important;
  }
`

const GroupCardTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colours.otherBlack};
`

const GroupCardMeta = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  margin-top: 4px;
`

const GroupCardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`

const SubSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px dashed
    ${({ $variant }) =>
      $variant === 'mapping'
        ? '#91caff'
        : $variant === 'products'
        ? '#d3adf7'
        : '#b7eb8f'};
  background: #ffffff;
`

const SubSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #595959;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`

const MappingPreviewBox = styled.div`
  margin-top: 8px;
  border: 1px solid #e6f4ff;
  border-radius: 8px;
  padding: 10px;
  background: #f7fbff;
`

const MappingCard = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 1fr);
  gap: 8px;
  background: #ffffff;
`

const EditorActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`
const { Option, OptGroup } = Select
const formatCurrencyRoundedUp = value => {
  const numeric = Number(value || 0)
  if (!Number.isFinite(numeric) || numeric <= 0) return '0.00'
  const pennies = Math.ceil(numeric * 100)
  return (pennies / 100).toFixed(2)
}
const FieldLabel = ({ text, required = false }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span>{text}</span>
    <span
      style={{
        fontSize: 11,
        padding: '1px 6px',
        borderRadius: 999,
        color: required ? '#cf1322' : '#595959',
        background: required ? '#fff1f0' : '#f5f5f5',
        border: `1px solid ${required ? '#ffa39e' : '#d9d9d9'}`,
      }}
    >
      {required ? 'Required' : 'Optional'}
    </span>
  </span>
)

const LinkProductsModal = ({
  visible = false,
  venueId = '',
  products = [],
  linkedGroups = [],
  onCancel = () => {},
  onCreate = () => {},
  onUpdate = () => {},
  onDelete = () => {},
  onDuplicate = () => {},
}) => {
  const [editingGroupId, setEditingGroupId] = useState(null)
  const [groupName, setGroupName] = useState('')
  const [displaySku, setDisplaySku] = useState('')
  const [displayUoM, setDisplayUoM] = useState('')
  const [linkedProductIds, setLinkedProductIds] = useState([])
  const [memberMappings, setMemberMappings] = useState({})
  const [productOverrides, setProductOverrides] = useState({})
  const [manualStockValue, setManualStockValue] = useState(0)
  const [note, setNote] = useState('')
  const [conversionEditor, setConversionEditor] = useState({
    visible: false,
    mode: 'create',
    productId: '',
    venueProductId: '',
    conversionId: '',
    label: '',
    toQty: 0,
    toUnit: '',
    size: '',
    saving: false,
  })

  const effectiveProducts = useMemo(
    () =>
      productsForLinkedProfileEdit(products, linkedGroups).map(product => {
        const id = String(
          product?._id || product?.id || product?.productId || '',
        )
        const override = productOverrides[id]
        if (!override) return product
        return {
          ...product,
          ...override,
          conversions: Array.isArray(override?.conversions)
            ? override.conversions
            : product?.conversions,
        }
      }),
    [products, linkedGroups, productOverrides],
  )

  const productMapById = useMemo(() => {
    const map = new Map()
    ;(effectiveProducts || []).forEach(product => {
      const id = product?._id || product?.id || product?.productId
      if (!id) return
      map.set(String(id), product)
    })
    return map
  }, [effectiveProducts])

  const conversionEditorProduct = useMemo(() => {
    if (!conversionEditor?.productId) return null
    return productMapById.get(String(conversionEditor.productId)) || null
  }, [conversionEditor?.productId, productMapById])

  const selectedProducts = useMemo(
    () =>
      (linkedProductIds || [])
        .map(id => productMapById.get(String(id)))
        .filter(Boolean),
    [linkedProductIds, productMapById],
  )

  const normalizedMemberMappings = useMemo(
    () =>
      selectedProducts.map(product => {
        const productId = String(
          product?._id || product?.id || product?.productId || '',
        )
        const baseUoM = String(product?.UoM || '').trim()
        const rawMapping = memberMappings?.[productId] || {}
        const selectedConversionId = rawMapping?.selectedConversionId || null
        const conversions = Array.isArray(product?.conversions)
          ? product.conversions
          : []
        const selectedConversion = conversions.find(conversion => {
          const candidateId =
            conversion?.conversionId || conversion?._id || conversion?.id
          return (
            String(candidateId || '') === String(selectedConversionId || '')
          )
        })
        const selectedToUnit = String(
          rawMapping?.selectedToUnit || selectedConversion?.toUnit || '',
        ).trim()
        const selectedToQty = Number(
          rawMapping?.selectedToQty || selectedConversion?.toQty || 0,
        )
        const selectedConversionLabel = String(
          rawMapping?.selectedConversionLabel ||
            selectedConversion?.label ||
            '',
        ).trim()
        const useBaseUoM = Boolean(
          rawMapping?.useBaseUoM || !selectedConversionId,
        )
        const resolvedUoM = useBaseUoM ? baseUoM : selectedToUnit
        return {
          productId,
          baseUoM,
          selectedConversionId,
          selectedConversionLabel,
          selectedToUnit,
          selectedToQty,
          useBaseUoM,
          resolvedUoM,
          title: product?.title || 'Untitled',
          supplierTitle: product?.supplierTitle || 'No supplier',
        }
      }),
    [selectedProducts, memberMappings],
  )

  const availableConversionOptions = useMemo(
    () =>
      normalizedMemberMappings
        .filter(
          mapping =>
            !mapping.useBaseUoM &&
            mapping.selectedConversionId &&
            mapping.selectedToUnit &&
            Number(mapping.selectedToQty) > 0,
        )
        .map(mapping => ({
          conversionId: mapping.selectedConversionId,
          label: mapping.selectedConversionLabel || 'Conversion',
          toUnit: mapping.selectedToUnit,
          toQty: Number(mapping.selectedToQty) || 0,
          sourceProductId: mapping.productId,
        })),
    [normalizedMemberMappings],
  )

  const conversionUnitPriceHints = useMemo(() => {
    const hints = new Map()
    normalizedMemberMappings.forEach(mapping => {
      const product = productMapById.get(String(mapping.productId || ''))
      if (!product) return
      const sourcePrice = Number(product?.price || 0)
      if (!Number.isFinite(sourcePrice) || sourcePrice <= 0) return
      if (mapping.useBaseUoM) {
        hints.set(String(mapping.productId), {
          sourcePrice,
          perUnitPrice: sourcePrice,
          label: `Base UoM (${mapping.baseUoM || 'N/A'})`,
          toQty: 1,
        })
        return
      }
      const toQty = Number(mapping.selectedToQty || 0)
      if (!Number.isFinite(toQty) || toQty <= 0) return
      hints.set(String(mapping.productId), {
        sourcePrice,
        perUnitPrice: sourcePrice / toQty,
        label: mapping.selectedToUnit || 'converted unit',
        toQty,
      })
    })
    return hints
  }, [normalizedMemberMappings, productMapById])

  const mappingCalculationPreview = useMemo(() => {
    if (!normalizedMemberMappings.length) {
      return { summary: '', details: [] }
    }
    const details = normalizedMemberMappings.map(mapping => {
      const product = productMapById.get(String(mapping.productId || ''))
      const name = String(product?.title || 'Unnamed product').trim()
      const size = String(product?.size || '').trim()
      const baseSuffix = size
        ? ` (${size}${mapping?.baseUoM ? ` ${mapping.baseUoM}` : ''})`
        : mapping?.baseUoM
        ? ` (${mapping.baseUoM})`
        : ''
      const factor = mapping.useBaseUoM
        ? 1
        : Number(mapping.selectedToQty || 0) > 0
        ? Number(mapping.selectedToQty || 0)
        : 1
      const unitLabel = mapping.useBaseUoM
        ? mapping.baseUoM || 'unit'
        : mapping.selectedToUnit || 'unit'
      return {
        label: `${name}${baseSuffix}`,
        factor,
        unitLabel,
      }
    })

    const units = [
      ...new Set(details.map(detail => String(detail.unitLabel || '').trim())),
    ]
    if (units.length === 1) {
      const total = details.reduce(
        (sum, detail) => sum + (Number(detail.factor) || 0),
        0,
      )
      const average = details.length > 0 ? total / details.length : 0
      const summary = `Average mapped quantity (1 source unit each) => ${Number(
        average,
      ).toFixed(2)} ${units[0]}`
      return { summary, details }
    }
    return {
      summary: '1 of each mapped product (mixed units):',
      details,
    }
  }, [normalizedMemberMappings, productMapById])

  const invalidMappings = useMemo(
    () =>
      normalizedMemberMappings.filter(mapping => {
        if (mapping.useBaseUoM) return false
        return (
          !mapping.selectedConversionId ||
          !String(mapping.selectedToUnit || '').trim() ||
          Number(mapping.selectedToQty) <= 0
        )
      }),
    [normalizedMemberMappings],
  )

  useEffect(() => {
    setMemberMappings(prev => {
      const nextMappings = {}
      selectedProducts.forEach(product => {
        const productId = String(
          product?._id || product?.id || product?.productId || '',
        )
        if (!productId) return
        const existing = prev?.[productId]
        if (existing) {
          nextMappings[productId] = existing
          return
        }
        nextMappings[productId] = {
          productId,
          baseUoM: String(product?.UoM || '').trim(),
          selectedConversionId: null,
          selectedConversionLabel: '',
          selectedToUnit: '',
          selectedToQty: 0,
          useBaseUoM: true,
        }
      })
      return nextMappings
    })
  }, [selectedProducts])

  useEffect(() => {
    if (!visible) {
      setEditingGroupId(null)
    }
  }, [visible])

  const canSubmit =
    String(groupName || '').trim().length > 0 &&
    String(displayUoM || '').trim().length > 0 &&
    Number(manualStockValue) > 0 &&
    Array.isArray(linkedProductIds) &&
    linkedProductIds.length >= 2 &&
    invalidMappings.length === 0

  const handleConfirm = async () => {
    if (!canSubmit) return
    const payload = {
      groupName: String(groupName || '').trim(),
      displayName: String(groupName || '').trim(),
      displaySku: String(displaySku || '').trim(),
      displayUoM: String(displayUoM || '').trim(),
      displayConversionOptions: availableConversionOptions,
      linkedProductIds,
      memberMappings: normalizedMemberMappings,
      manualStockValue: Number(manualStockValue) || 0,
      note: String(note || '').trim(),
    }
    if (editingGroupId) {
      await onUpdate(editingGroupId, payload)
    } else {
      await onCreate(payload)
    }
    resetState()
  }

  const resetState = () => {
    setEditingGroupId(null)
    setGroupName('')
    setDisplaySku('')
    setDisplayUoM('')
    setLinkedProductIds([])
    setMemberMappings({})
    setProductOverrides({})
    setManualStockValue(0)
    setNote('')
  }

  const handleEdit = group => {
    setEditingGroupId(group?._id || group?.id || null)
    setGroupName(group?.displayName || group?.groupName || '')
    setDisplaySku(group?.displaySku || '')
    setDisplayUoM(group?.displayUoM || '')
    setLinkedProductIds(
      (Array.isArray(group?.linkedProductIds) ? group.linkedProductIds : [])
        .map(id => String(id || '').trim())
        .filter(Boolean),
    )
    const nextMappings = {}
    ;(Array.isArray(group?.memberMappings) ? group.memberMappings : []).forEach(
      mapping => {
        const productId = String(mapping?.productId || '')
        if (!productId) return
        nextMappings[productId] = {
          productId,
          baseUoM: String(mapping?.baseUoM || '').trim(),
          selectedConversionId: mapping?.selectedConversionId || null,
          selectedConversionLabel: String(
            mapping?.selectedConversionLabel || '',
          ).trim(),
          selectedToUnit: String(mapping?.selectedToUnit || '').trim(),
          selectedToQty: Number(mapping?.selectedToQty) || 0,
          useBaseUoM: Boolean(mapping?.useBaseUoM),
        }
      },
    )
    setMemberMappings(nextMappings)
    setManualStockValue(Number(group?.manualStockValue) || 0)
    setNote(group?.note || '')
  }

  const handleChangeMemberConversion = (productId, value) => {
    const product = productMapById.get(String(productId))
    const conversions = Array.isArray(product?.conversions)
      ? product.conversions
      : []
    if (value === '__base__') {
      setMemberMappings(prev => ({
        ...prev,
        [productId]: {
          productId,
          baseUoM: String(product?.UoM || '').trim(),
          selectedConversionId: null,
          selectedConversionLabel: '',
          selectedToUnit: '',
          selectedToQty: 0,
          useBaseUoM: true,
        },
      }))
      return
    }
    const selected = conversions.find(conversion => {
      const id = conversion?.conversionId || conversion?._id || conversion?.id
      return String(id || '') === String(value || '')
    })
    if (!selected) return
    const conversionId = selected?.conversionId || selected?._id || selected?.id
    setMemberMappings(prev => ({
      ...prev,
      [productId]: {
        productId,
        baseUoM: String(product?.UoM || '').trim(),
        selectedConversionId: conversionId,
        selectedConversionLabel: String(selected?.label || 'Conversion').trim(),
        selectedToUnit: String(selected?.toUnit || '').trim(),
        selectedToQty: Number(selected?.toQty || 0),
        useBaseUoM: false,
      },
    }))
  }

  const applyVenueProductConversionUpdate = (venueProductId, conversions) => {
    if (!venueProductId) return
    setProductOverrides(prev => {
      const next = { ...prev }
      selectedProducts.forEach(product => {
        const selectedId = String(
          product?._id || product?.id || product?.productId || '',
        )
        const selectedVenueProductId = String(product?.venueProductId || '')
        if (selectedId && selectedVenueProductId === String(venueProductId)) {
          next[selectedId] = {
            ...(next[selectedId] || {}),
            venueProductId: String(venueProductId),
            conversions,
          }
        }
      })
      return next
    })
  }

  const ensureVenueProductForProduct = async product => {
    const existingVenueProductId = String(product?.venueProductId || '').trim()
    if (existingVenueProductId) return existingVenueProductId
    if (!venueId) return ''

    const payload = {
      venue: venueId,
      title: String(product?.title || 'New Product').trim(),
      UoM: String(product?.UoM || '').trim(),
      supplierTitle: String(product?.supplierTitle || '').trim(),
      type: String(product?.type || 'Default Type').trim() || 'Default Type',
      zone: 'Default Zone',
      defaultQuantity: 1,
      isFavourite: false,
      conversions: [],
    }

    const createdVenueProduct = await API.post(
      'api',
      `venue/${venueId}/product`,
      {
        body: payload,
      },
    )
    const createdVenueProductId = String(createdVenueProduct?._id || '').trim()
    if (!createdVenueProductId) return ''

    const productId = String(
      product?._id || product?.id || product?.productId || '',
    )
    if (productId) {
      setProductOverrides(prev => ({
        ...prev,
        [productId]: {
          ...(prev[productId] || {}),
          venueProductId: createdVenueProductId,
          title: createdVenueProduct?.title || product?.title || 'New Product',
          UoM: createdVenueProduct?.UoM || product?.UoM || '',
          size: createdVenueProduct?.size || product?.size || '',
          conversions: Array.isArray(createdVenueProduct?.conversions)
            ? createdVenueProduct.conversions
            : [],
        },
      }))
    }

    return createdVenueProductId
  }

  const openCreateConversionEditor = product => {
    const productId = String(
      product?._id || product?.id || product?.productId || '',
    )
    const venueProductId = String(product?.venueProductId || '')
    setConversionEditor({
      visible: true,
      mode: 'create',
      productId,
      venueProductId,
      conversionId: '',
      label: '',
      toQty: '',
      toUnit: '',
      size: String(product?.size || product?.defaultSize || '').trim(),
      saving: false,
    })
  }

  const openEditConversionEditor = (product, conversion) => {
    const productId = String(
      product?._id || product?.id || product?.productId || '',
    )
    const venueProductId = String(product?.venueProductId || '')
    const conversionId = String(
      conversion?.conversionId || conversion?._id || conversion?.id || '',
    )
    setConversionEditor({
      visible: true,
      mode: 'edit',
      productId,
      venueProductId,
      conversionId,
      label: String(conversion?.label || '').trim(),
      toQty: Number(conversion?.toQty || 0),
      toUnit: String(conversion?.toUnit || '').trim(),
      size: String(conversion?.size || product?.size || '').trim(),
      saving: false,
    })
  }

  const handleSaveConversion = async () => {
    const { mode, venueProductId, conversionId, label, toQty, toUnit, size } =
      conversionEditor
    const sourceProduct = productMapById.get(
      String(conversionEditor?.productId || ''),
    )
    let resolvedVenueProductId = String(venueProductId || '').trim()
    if (!resolvedVenueProductId && sourceProduct) {
      resolvedVenueProductId = await ensureVenueProductForProduct(sourceProduct)
    }
    if (!venueId || !resolvedVenueProductId) {
      message.error('Unable to create a venue product link for this item.')
      return
    }
    if (
      !String(label || '').trim() ||
      Number(toQty) <= 0 ||
      !String(toUnit || '').trim()
    ) {
      message.error('Please enter label, qty (>0), and unit.')
      return
    }
    setConversionEditor(prev => ({ ...prev, saving: true }))
    try {
      const body = {
        label: String(label || '').trim(),
        toQty: Number(toQty),
        toUnit: String(toUnit || '').trim(),
        size: String(
          size ||
            sourceProduct?.size ||
            sourceProduct?.defaultSize ||
            conversionEditorProduct?.size ||
            '1',
        ).trim(),
      }
      const response =
        mode === 'edit' && conversionId
          ? await API.patch(
              'api2',
              `venue/${venueId}/products/${resolvedVenueProductId}/conversions/${conversionId}`,
              { body },
            )
          : await API.post(
              'api2',
              `venue/${venueId}/products/${resolvedVenueProductId}/conversions`,
              {
                body,
              },
            )
      const nextConversions = Array.isArray(response?.venueProduct?.conversions)
        ? response.venueProduct.conversions
        : []
      applyVenueProductConversionUpdate(resolvedVenueProductId, nextConversions)
      message.success(
        mode === 'edit' ? 'Conversion updated.' : 'Conversion created.',
      )
      setConversionEditor(prev => ({
        ...prev,
        venueProductId: resolvedVenueProductId,
        visible: false,
        saving: false,
      }))
    } catch (error) {
      console.error('Failed to save conversion:', error)
      message.error(
        error?.response?.data?.message || 'Failed to save conversion.',
      )
      setConversionEditor(prev => ({ ...prev, saving: false }))
    }
  }

  const handleDeleteConversion = async (product, conversion) => {
    const venueProductId = String(product?.venueProductId || '')
    const conversionId = String(
      conversion?.conversionId || conversion?._id || conversion?.id || '',
    )
    if (!venueId || !venueProductId || !conversionId) return
    try {
      const response = await API.del(
        'api2',
        `venue/${venueId}/products/${venueProductId}/conversions/${conversionId}`,
      )
      const nextConversions = Array.isArray(response?.venueProduct?.conversions)
        ? response.venueProduct.conversions
        : []
      applyVenueProductConversionUpdate(venueProductId, nextConversions)
      message.success('Conversion deleted.')
    } catch (error) {
      console.error('Failed to delete conversion:', error)
      message.error(
        error?.response?.data?.message || 'Failed to delete conversion.',
      )
    }
  }

  const sortedLinkedGroups = useMemo(
    () =>
      [...(linkedGroups || [])].sort((a, b) =>
        String(a?.displayName || a?.groupName || '').localeCompare(
          String(b?.displayName || b?.groupName || ''),
        ),
      ),
    [linkedGroups],
  )

  return (
    <>
      <Modal
        title='Link Products'
        visible={visible}
        onCancel={() => {
          resetState()
          onCancel()
        }}
        footer={null}
        width={980}
        destroyOnClose
      >
        <ModalBody>
          <WayfindingBanner>
            <strong>Link products:</strong> combine supplier lines into one
            stock count row. Blue = existing profiles, purple = editor, green =
            display details, dashed purple = products, dashed blue = unit
            mapping.
          </WayfindingBanner>

          <Section $variant='list'>
            <SectionHeader>
              <SectionIcon $variant='list'>
                <TeamOutlined />
              </SectionIcon>
              <div>
                <SectionTitle>Existing linked profiles</SectionTitle>
                <SectionHint>
                  Edit, duplicate, or delete linked product profiles.
                </SectionHint>
              </div>
              <Tag color='blue' style={{ marginLeft: 'auto' }}>
                {sortedLinkedGroups.length} profile
                {sortedLinkedGroups.length === 1 ? '' : 's'}
              </Tag>
            </SectionHeader>
            <GroupsList
              style={{ maxHeight: 220, overflowY: 'auto' }}
              dataSource={sortedLinkedGroups}
              locale={{ emptyText: 'No linked product profiles created yet.' }}
              renderItem={group => {
                const linkedCount = Array.isArray(group?.linkedProductIds)
                  ? group.linkedProductIds.length
                  : 0
                return (
                  <List.Item>
                    <GroupCardTitle>
                      {group?.displayName ||
                        group?.groupName ||
                        'Untitled linked profile'}
                    </GroupCardTitle>
                    <GroupCardMeta>
                      {group?.displayUoM || 'No UoM'} • £
                      {Number(group?.manualStockValue || 0).toFixed(2)}
                    </GroupCardMeta>
                    <div style={{ marginTop: 6 }}>
                      <Tag color='geekblue'>
                        {linkedCount} linked product
                        {linkedCount === 1 ? '' : 's'}
                      </Tag>
                    </div>
                    <GroupCardActions>
                      <Button icon='edit' onClick={() => handleEdit(group)}>
                        Edit
                      </Button>
                      <Button icon='copy' onClick={() => onDuplicate(group)}>
                        Duplicate
                      </Button>
                      <Popconfirm
                        title='Delete this linked product profile?'
                        okText='Delete'
                        cancelText='Cancel'
                        onConfirm={() => onDelete(group?._id || group?.id)}
                      >
                        <Button type='danger' icon='delete'>
                          Delete
                        </Button>
                      </Popconfirm>
                    </GroupCardActions>
                  </List.Item>
                )
              }}
            />
          </Section>

          <Section $variant='editor'>
            <SectionHeader>
              <SectionIcon $variant='editor'>
                <FormOutlined />
              </SectionIcon>
              <div>
                <SectionTitle>
                  {editingGroupId
                    ? 'Edit linked profile'
                    : 'Create linked profile'}
                </SectionTitle>
                <SectionHint>
                  Only affects Stock Take — not your Catalogue.
                </SectionHint>
              </div>
              {editingGroupId ? (
                <Tag color='purple' style={{ marginLeft: 'auto' }}>
                  Editing
                </Tag>
              ) : null}
            </SectionHeader>

            <SubSection $variant='details'>
              <SubSectionTitle>
                <LinkOutlined style={{ marginRight: 6 }} />
                Display details
              </SubSectionTitle>
              <div style={{ marginTop: 14 }}>
                <Text strong>
                  <FieldLabel text='Display product name' required />
                </Text>
                <Input
                  style={{ marginTop: 6 }}
                  placeholder='e.g. Guinness 50L'
                  value={groupName}
                  onChange={event => setGroupName(event.target.value)}
                />
              </div>

              <div style={{ marginTop: 14 }}>
                <Text strong>
                  <FieldLabel text='Display SKU' />
                </Text>
                <Input
                  style={{ marginTop: 6 }}
                  placeholder='e.g. GNS-50L-LINKED'
                  value={displaySku}
                  onChange={event => setDisplaySku(event.target.value)}
                />
              </div>

              <div style={{ marginTop: 14 }}>
                <Text strong>
                  <FieldLabel text='Display UoM' required />
                </Text>
                <Input
                  style={{ marginTop: 6 }}
                  placeholder='e.g. Pint, Keg Eq, Bottle, Pack'
                  value={displayUoM}
                  onChange={event => setDisplayUoM(event.target.value)}
                />
                <Text
                  type='secondary'
                  style={{ display: 'block', marginTop: 4 }}
                >
                  Display label only. This does not change conversion math.
                </Text>
              </div>
            </SubSection>

            <SubSection $variant='products'>
              <SubSectionTitle>
                <ShoppingOutlined style={{ marginRight: 6 }} />
                Products to link
              </SubSectionTitle>
              <Select
                mode='multiple'
                style={{ width: '100%', marginTop: 6 }}
                placeholder='Select two or more products'
                value={linkedProductIds}
                onChange={value => setLinkedProductIds(value)}
                optionFilterProp='children'
              >
                {(effectiveProducts || []).map(product => {
                  const id = String(
                    product?._id || product?.id || product?.productId || '',
                  )
                  if (!id) return null
                  const title = product?.title || 'Untitled'
                  const supplier = product?.supplierTitle || 'No supplier'
                  const sku = product?.sku ? ` • ${product.sku}` : ''
                  return (
                    <Select.Option key={id} value={id}>
                      {`${title} (${supplier}${sku})`}
                    </Select.Option>
                  )
                })}
              </Select>
            </SubSection>

            <SubSection $variant='mapping'>
              <SubSectionTitle>
                <SwapOutlined style={{ marginRight: 6 }} />
                Unit mapping
              </SubSectionTitle>
              {mappingCalculationPreview.summary ? (
                <MappingPreviewBox>
                  <Text strong>{mappingCalculationPreview.summary}</Text>
                  <div style={{ marginTop: 6 }}>
                    {mappingCalculationPreview.details.map((detail, index) => (
                      <Text
                        key={`mapping-preview-${detail.label}-${index}`}
                        type='secondary'
                        style={{ display: 'block' }}
                      >
                        {`- ${detail.label}: ${Number(detail.factor).toFixed(
                          2,
                        )} ${detail.unitLabel}`}
                      </Text>
                    ))}
                  </div>
                </MappingPreviewBox>
              ) : null}
              <div style={{ marginTop: 6, display: 'grid', gap: 8 }}>
                {selectedProducts.length ? (
                  selectedProducts.map(product => {
                    const productId = String(
                      product?._id || product?.id || product?.productId || '',
                    )
                    const mapping = memberMappings?.[productId]
                    const conversions = Array.isArray(product?.conversions)
                      ? product.conversions
                      : []
                    const selectedConversionId = String(
                      mapping?.selectedConversionId || '',
                    )
                    const hasSelectedConversionInOptions = conversions.some(
                      conversion => {
                        const conversionId =
                          conversion?.conversionId ||
                          conversion?._id ||
                          conversion?.id
                        return (
                          String(conversionId || '') === selectedConversionId
                        )
                      },
                    )
                    const fallbackSelectedConversion =
                      selectedConversionId && !hasSelectedConversionInOptions
                        ? [
                            {
                              conversionId: selectedConversionId,
                              label: String(
                                mapping?.selectedConversionLabel ||
                                  'Saved conversion',
                              ).trim(),
                              toUnit: String(
                                mapping?.selectedToUnit || '',
                              ).trim(),
                              toQty: Number(mapping?.selectedToQty || 0),
                              __fallback: true,
                            },
                          ]
                        : []
                    const resolvedConversions = [
                      ...conversions,
                      ...fallbackSelectedConversion,
                    ]
                    const value = mapping?.useBaseUoM
                      ? '__base__'
                      : String(mapping?.selectedConversionId || '')
                    return (
                      <MappingCard key={`mapping-${productId}`}>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {product?.title || 'Untitled'}
                          </div>
                          <Text type='secondary'>
                            {`${
                              product?.supplierTitle || 'No supplier'
                            } • Base: ${product?.UoM || 'N/A'}`}
                          </Text>
                          {(() => {
                            const hint = conversionUnitPriceHints.get(productId)
                            if (!hint) return null
                            if (hint.toQty === 1) {
                              return (
                                <Text
                                  type='secondary'
                                  style={{ display: 'block', marginTop: 4 }}
                                >
                                  {`Current base unit cost: £${formatCurrencyRoundedUp(
                                    hint.perUnitPrice,
                                  )} per ${hint.label}`}
                                </Text>
                              )
                            }
                            return (
                              <Text
                                type='secondary'
                                style={{ display: 'block', marginTop: 4 }}
                              >
                                {`Estimated converted unit cost: £${formatCurrencyRoundedUp(
                                  hint.perUnitPrice,
                                )} per ${
                                  hint.label
                                } (source £${formatCurrencyRoundedUp(
                                  hint.sourcePrice,
                                )} / ${Number(hint.toQty || 0).toFixed(2)})`}
                              </Text>
                            )
                          })()}
                        </div>
                        <Select
                          value={value}
                          onChange={next =>
                            handleChangeMemberConversion(productId, next)
                          }
                        >
                          <Select.Option value='__base__'>
                            {`Use base UoM (${product?.UoM || 'N/A'})`}
                          </Select.Option>
                          {resolvedConversions.map(conversion => {
                            const conversionId =
                              conversion?.conversionId ||
                              conversion?._id ||
                              conversion?.id
                            if (!conversionId) return null
                            const label = String(
                              conversion?.label || 'Conversion',
                            ).trim()
                            const toQty = Number(conversion?.toQty || 0)
                            const toUnit = String(
                              conversion?.toUnit || '',
                            ).trim()
                            return (
                              <Select.Option
                                key={`${productId}-${conversionId}`}
                                value={conversionId}
                              >
                                {`${label} (${toQty} ${toUnit})${
                                  conversion?.__fallback ? ' [saved]' : ''
                                }`}
                              </Select.Option>
                            )
                          })}
                        </Select>
                        <div
                          style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                        >
                          <Button
                            size='small'
                            onClick={() => openCreateConversionEditor(product)}
                          >
                            Add conversion
                          </Button>
                          {conversions.length > 0 ? (
                            <Text
                              type='secondary'
                              style={{ alignSelf: 'center' }}
                            >
                              {`${conversions.length} saved conversion(s)`}
                            </Text>
                          ) : null}
                        </div>
                        {conversions.length > 0 ? (
                          <div
                            style={{
                              border: '1px dashed #f0f0f0',
                              borderRadius: 6,
                              padding: 8,
                            }}
                          >
                            {conversions.map(conversion => {
                              const conversionId =
                                conversion?.conversionId ||
                                conversion?._id ||
                                conversion?.id
                              if (!conversionId) return null
                              return (
                                <div
                                  key={`conversion-row-${productId}-${conversionId}`}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginBottom: 6,
                                  }}
                                >
                                  <Text>{`${
                                    conversion?.label || 'Conversion'
                                  } (${Number(conversion?.toQty || 0)} ${String(
                                    conversion?.toUnit || '',
                                  ).trim()})`}</Text>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <Button
                                      size='small'
                                      type='link'
                                      onClick={() =>
                                        openEditConversionEditor(
                                          product,
                                          conversion,
                                        )
                                      }
                                    >
                                      Edit
                                    </Button>
                                    <Popconfirm
                                      title='Delete this conversion?'
                                      okText='Delete'
                                      cancelText='Cancel'
                                      onConfirm={() =>
                                        handleDeleteConversion(
                                          product,
                                          conversion,
                                        )
                                      }
                                    >
                                      <Button
                                        size='small'
                                        type='link'
                                        style={{ color: '#f5222d' }}
                                      >
                                        Delete
                                      </Button>
                                    </Popconfirm>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : null}
                      </MappingCard>
                    )
                  })
                ) : (
                  <Text type='secondary'>
                    Select products first to configure mappings.
                  </Text>
                )}
              </div>
              {invalidMappings.length > 0 ? (
                <Text type='danger' style={{ display: 'block', marginTop: 8 }}>
                  One or more selected mappings are incomplete. Choose base UoM
                  or a valid conversion.
                </Text>
              ) : null}
            </SubSection>

            <SubSection $variant='details'>
              <SubSectionTitle>Pricing & notes</SubSectionTitle>
              <div style={{ marginTop: 14 }}>
                <Text strong>Available conversions from linked products</Text>
                <div style={{ marginTop: 6 }}>
                  {availableConversionOptions.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {availableConversionOptions.map(option => (
                        <li key={`linked-conv-${option.conversionId}`}>
                          {`${option.label} (${option.toQty} ${option.toUnit})`}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Text type='secondary'>
                      {displayUoM
                        ? `No conversions available for ${displayUoM}.`
                        : 'Choose products and a display UoM to see conversion options.'}
                    </Text>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <Text strong>
                  <FieldLabel text='Product Price' required />
                </Text>
                <InputNumber
                  style={{ width: '100%', marginTop: 6 }}
                  min={0.01}
                  step={0.01}
                  value={manualStockValue}
                  onChange={value => setManualStockValue(value ?? 0)}
                />
                <Text
                  type='secondary'
                  style={{ display: 'block', marginTop: 6 }}
                >
                  Set this to the price of one displayed/converted unit (not the
                  full source pack). Example: if 1 keg costs £100 and maps to
                  100 pints, set Product Price to £1.00. This price is used to
                  calculate stock values for the linked line.
                </Text>
                {Number(manualStockValue) <= 0 ? (
                  <Text
                    type='danger'
                    style={{ display: 'block', marginTop: 4 }}
                  >
                    Enter a product price greater than 0.
                  </Text>
                ) : null}
              </div>

              <div style={{ marginTop: 14 }}>
                <Text strong>
                  <FieldLabel text='Note' />
                </Text>
                <Input.TextArea
                  rows={3}
                  style={{ marginTop: 6 }}
                  placeholder='Optional note for this linked group'
                  value={note}
                  onChange={event => setNote(event.target.value)}
                />
              </div>
            </SubSection>

            <EditorActions>
              <Button
                onClick={() => {
                  resetState()
                  onCancel()
                }}
              >
                Cancel
              </Button>
              <Button
                type='primary'
                disabled={!canSubmit}
                onClick={handleConfirm}
              >
                {editingGroupId
                  ? 'Save linked profile'
                  : 'Create linked profile'}
              </Button>
            </EditorActions>
          </Section>
        </ModalBody>
      </Modal>
      <Modal
        title={
          conversionEditor.mode === 'edit'
            ? 'Edit conversion'
            : 'Add conversion'
        }
        visible={conversionEditor.visible}
        onCancel={() =>
          setConversionEditor(prev => ({ ...prev, visible: false }))
        }
        onOk={handleSaveConversion}
        confirmLoading={conversionEditor.saving}
        destroyOnClose
      >
        <Text type='secondary' style={{ display: 'block', marginBottom: 12 }}>
          Define how the product&apos;s original pack/size converts into a unit
          you can count in stock takes. Example:{' '}
          <strong>1 x 2.5kg = 2500 g</strong>.
        </Text>
        {conversionEditorProduct ? (
          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 10,
              marginBottom: 12,
              background: '#fafafa',
            }}
          >
            <Text strong>
              {conversionEditorProduct?.title || 'Selected product'}
            </Text>
            <div>
              <Text type='secondary'>
                {`Base size: ${
                  conversionEditor.size ||
                  conversionEditorProduct?.size ||
                  'N/A'
                } • Base UoM: ${conversionEditorProduct?.UoM || 'N/A'}`}
              </Text>
            </div>
          </div>
        ) : null}
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <Text strong>Label</Text>
            <Input
              style={{ marginTop: 6 }}
              value={conversionEditor.label}
              onChange={event =>
                setConversionEditor(prev => ({
                  ...prev,
                  label: event.target.value,
                }))
              }
              placeholder='e.g. 1x2.5kg => 2500 g'
            />
          </div>
          <div>
            <Text strong>Conversion rule</Text>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                gap: 8,
                alignItems: 'center',
                marginTop: 6,
              }}
            >
              <Text
                type='secondary'
                style={{
                  marginBottom: 0,
                  gridColumn: '1 / span 1',
                  whiteSpace: 'nowrap',
                }}
              >
                From
              </Text>
              <InputNumber
                disabled
                value={1}
                style={{ width: '100%', gridColumn: '2 / span 1' }}
              />
              <Input
                disabled
                value={
                  conversionEditor.size || conversionEditorProduct?.size || ''
                }
                style={{ width: '100%', gridColumn: '3 / span 1' }}
              />
              <Text
                type='secondary'
                style={{
                  marginBottom: 0,
                  gridColumn: '4 / span 1',
                  whiteSpace: 'nowrap',
                }}
              >
                To
              </Text>
              <InputNumber
                style={{ width: '100%', gridColumn: '5 / span 1' }}
                min={0.0001}
                value={conversionEditor.toQty}
                placeholder='Qty'
                onChange={value =>
                  setConversionEditor(prev => ({
                    ...prev,
                    toQty: value === null || value === undefined ? '' : value,
                  }))
                }
              />
              <Select
                style={{ width: '100%', gridColumn: '6 / span 1' }}
                value={conversionEditor.toUnit || undefined}
                placeholder='Unit'
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ minWidth: 220 }}
                onChange={value =>
                  setConversionEditor(prev => ({ ...prev, toUnit: value }))
                }
              >
                <OptGroup label='Weight'>
                  <Option value='kg'>Kilogram (kg)</Option>
                  <Option value='g'>Gram (g)</Option>
                  <Option value='mg'>Milligram (mg)</Option>
                  <Option value='lb'>Pound (lb)</Option>
                  <Option value='oz'>Ounce (oz)</Option>
                </OptGroup>
                <OptGroup label='Volume'>
                  <Option value='ltr'>Liter (ltr)</Option>
                  <Option value='ml'>Milliliter (ml)</Option>
                  <Option value='cl'>Centiliter (cl)</Option>
                  <Option value='gal'>Gallon (gal)</Option>
                  <Option value='floz'>Fluid Ounce (fl oz)</Option>
                </OptGroup>
                <OptGroup label='Count'>
                  <Option value='unit'>Unit</Option>
                  <Option value='piece'>Piece</Option>
                  <Option value='item'>Item</Option>
                  <Option value='pack'>Pack</Option>
                  <Option value='box'>Box</Option>
                </OptGroup>
              </Select>
            </div>
            <Text type='secondary' style={{ display: 'block', marginTop: 6 }}>
              This conversion becomes available in both Link Products and Menu
              Builder.
            </Text>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default LinkProductsModal
